// Signup page. Email/password + Google. Creates the Firebase user.
//
// Note: V1 auto-creating a Personal Team after signup requires a Function
// (#6) that has Admin SDK access to write the team + member docs in a single
// transaction (security rules don't allow client-side writes to those
// without a member doc existing). For 8a, signup creates the Firebase user
// only; the team-creation step is wired in #6.

import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button.jsx'
import { auth } from '../../lib/firebase.js'

export function Signup() {
  const navigate = useNavigate()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleEmailSignup(e) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password)
      if (displayName) {
        await updateProfile(cred.user, { displayName })
      }
      navigate('/teams', { replace: true })
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGoogle() {
    setError(null)
    setSubmitting(true)
    try {
      await signInWithPopup(auth, new GoogleAuthProvider())
      navigate('/teams', { replace: true })
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main id="main-content" className="mx-auto max-w-md px-6 py-16">
      <h1 className="mb-2 text-2xl font-semibold text-[var(--color-basalt)]">Create an account</h1>
      <p className="mb-8 text-sm text-[var(--color-schist)]">
        Cairn is free during beta. No credit card required.
      </p>

      <form onSubmit={handleEmailSignup} className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
            Name (optional)
          </span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
            Password (min 8 chars)
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            className="w-full rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-2 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
          />
        </label>

        {error && (
          <p className="text-sm text-[var(--color-failed)]" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" disabled={submitting} variant="primary" className="w-full">
          {submitting ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <hr className="flex-1 border-[var(--color-sandstone)]" />
        <span className="text-xs uppercase tracking-wider text-[var(--color-schist)]">or</span>
        <hr className="flex-1 border-[var(--color-sandstone)]" />
      </div>

      <Button onClick={handleGoogle} variant="outline" disabled={submitting} className="w-full">
        Continue with Google
      </Button>

      <p className="mt-8 text-sm text-[var(--color-schist)]">
        Already have an account?{' '}
        <Link
          to="/auth/login"
          className="text-[var(--color-terracotta)] underline-offset-4 hover:underline"
        >
          Sign in
        </Link>
      </p>
    </main>
  )
}
